import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MessageCircle, Send, User, Users, Briefcase, Building, Mail, Phone,
  CheckCircle, Circle, ArrowLeft, Clock, Package, RefreshCw
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';

import { API_URL } from '../../../config/api';

const channelConfig = {
  rh: { 
    name: 'Responsable RH', 
    icon: Users,
    color: 'from-purple-500 to-purple-600',
    bgColor: 'bg-purple-100',
    textColor: 'text-purple-600',
    description: 'Recrutement assistantes'
  },
  projet: { 
    name: 'Chef de Projet', 
    icon: Briefcase,
    color: 'from-blue-500 to-blue-600',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-600',
    description: 'Paramétrage CRM & IA'
  }
};

export default function AdminClientChatPage() {
  const [activeChannel, setActiveChannel] = useState('rh');
  const [conversations, setConversations] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [stats, setStats] = useState({ unread_rh: 0, unread_projet: 0, clients_onboarding: 0 });
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchStats();
    fetchConversations();
  }, [activeChannel]);

  useEffect(() => {
    if (selectedClient) {
      fetchMessages();
    }
  }, [selectedClient, activeChannel]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchStats = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/admin-client-chat/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setStats(await response.json());
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchConversations = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin-client-chat/conversations?channel=${activeChannel}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setConversations(data);
        // Select first client if none selected
        if (!selectedClient && data.length > 0) {
          setSelectedClient(data[0].client_id);
        }
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token || !selectedClient) return;

    try {
      const response = await fetch(
        `${API_URL}/api/admin-client-chat/conversations/${selectedClient}/${activeChannel}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
        // Refresh stats and conversations to update unread counts
        fetchStats();
        fetchConversations();
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending || !selectedClient) return;

    const token = localStorage.getItem('admin_token');
    if (!token) return;

    setSending(true);
    try {
      const params = new URLSearchParams({ content: newMessage });
      const response = await fetch(
        `${API_URL}/api/admin-client-chat/messages/${selectedClient}/${activeChannel}?${params}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        setNewMessage('');
        fetchMessages();
      } else {
        toast.error('Erreur lors de l\'envoi');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const today = new Date();
    const isToday = date.toDateString() === today.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const selectedConversation = conversations.find(c => c.client_id === selectedClient);
  const channelInfo = channelConfig[activeChannel];

  return (
    <div className="space-y-6" data-testid="admin-client-chat-page">
      {/* Header with stats */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tchat Clients</h1>
          <p className="text-gray-500">Communiquez avec tes clients en onboarding</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-lg">
            <Clock className="w-4 h-4" />
            <span className="font-medium">{stats.clients_onboarding} clients en onboarding</span>
          </div>
          <Button variant="outline" onClick={() => { fetchStats(); fetchConversations(); }}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Channel Tabs */}
      <div className="flex gap-2 bg-gray-100 p-1 rounded-xl w-fit">
        {Object.entries(channelConfig).map(([id, config]) => {
          const Icon = config.icon;
          const unread = id === 'rh' ? stats.unread_rh : stats.unread_projet;
          return (
            <button
              key={id}
              onClick={() => { setActiveChannel(id); setSelectedClient(null); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                activeChannel === id
                  ? `bg-gradient-to-r ${config.color} text-white shadow-md`
                  : 'text-gray-600 hover:bg-gray-200'
              }`}
              data-testid={`tab-${id}`}
            >
              <Icon className="w-4 h-4" />
              <span className="font-medium">{config.name}</span>
              {unread > 0 && (
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  activeChannel === id ? 'bg-white text-purple-600' : 'bg-red-500 text-white'
                }`}>
                  {unread}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Main Chat Area */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="flex h-[600px]">
          {/* Clients List */}
          <div className="w-80 border-r border-gray-200 flex flex-col">
            <div className="p-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-700">Conversations</h3>
            </div>
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-600"></div>
                </div>
              ) : conversations.length === 0 ? (
                <div className="p-6 text-center text-gray-400">
                  <MessageCircle className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p>Aucune conversation</p>
                </div>
              ) : (
                conversations.map(conv => (
                  <button
                    key={conv.client_id}
                    onClick={() => setSelectedClient(conv.client_id)}
                    className={`w-full p-4 text-left border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                      selectedClient === conv.client_id ? 'bg-blue-50 border-l-4 border-l-blue-600' : ''
                    }`}
                    data-testid={`client-${conv.client_id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="font-bold text-gray-600">
                          {conv.client_name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h4 className="font-semibold text-gray-900 truncate">{conv.client_name}</h4>
                          {conv.unread_count_admin > 0 && (
                            <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                              {conv.unread_count_admin}
                            </span>
                          )}
                        </div>
                        {conv.client_company && (
                          <p className="text-xs text-gray-500 flex items-center gap-1">
                            <Building className="w-3 h-3" />
                            {conv.client_company}
                          </p>
                        )}
                        {conv.client_offer && (
                          <p className="text-xs text-blue-600 flex items-center gap-1 mt-1">
                            <Package className="w-3 h-3" />
                            {conv.client_offer}
                          </p>
                        )}
                        {conv.last_message && (
                          <p className="text-sm text-gray-400 truncate mt-1">{conv.last_message}</p>
                        )}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Chat Messages */}
          <div className="flex-1 flex flex-col">
            {!selectedClient ? (
              <div className="flex-1 flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <MessageCircle className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">Sélectionnez une conversation</p>
                </div>
              </div>
            ) : (
              <>
                {/* Chat Header */}
                <div className={`bg-gradient-to-r ${channelInfo?.color} text-white px-6 py-4`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                        <span className="font-bold">{selectedConversation?.client_name?.charAt(0)}</span>
                      </div>
                      <div>
                        <h3 className="font-bold">{selectedConversation?.client_name}</h3>
                        <p className="text-sm text-white/80">
                          {selectedConversation?.client_company} • {selectedConversation?.client_offer}
                        </p>
                      </div>
                    </div>
                    {selectedConversation?.client_email && (
                      <div className="flex items-center gap-4 text-sm text-white/80">
                        <span className="flex items-center gap-1">
                          <Mail className="w-4 h-4" />
                          {selectedConversation.client_email}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                  {messages.map((message, index) => {
                    const isAdmin = message.sender_type === 'admin';
                    const showDate = index === 0 || 
                      new Date(message.created_at).toDateString() !== 
                      new Date(messages[index - 1].created_at).toDateString();
                    
                    return (
                      <div key={message.id}>
                        {showDate && (
                          <div className="text-center my-4">
                            <span className="bg-gray-200 text-gray-600 text-xs px-3 py-1 rounded-full">
                              {new Date(message.created_at).toLocaleDateString('fr-FR', {
                                weekday: 'long', day: 'numeric', month: 'long'
                              })}
                            </span>
                          </div>
                        )}
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`max-w-[70%]`}>
                            {!isAdmin && (
                              <div className="flex items-center gap-2 mb-1">
                                <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center">
                                  <User className="w-3 h-3 text-gray-600" />
                                </div>
                                <span className="text-xs font-medium text-gray-600">
                                  {message.sender_name}
                                </span>
                              </div>
                            )}
                            <div className={`rounded-2xl px-4 py-3 ${
                              isAdmin
                                ? `bg-gradient-to-r ${channelInfo?.color} text-white rounded-br-md`
                                : 'bg-white text-gray-800 rounded-bl-md shadow-sm'
                            }`}>
                              <p className="whitespace-pre-wrap">{message.content}</p>
                            </div>
                            <div className={`flex items-center gap-1 mt-1 text-xs text-gray-400 ${
                              isAdmin ? 'justify-end' : ''
                            }`}>
                              <span>{formatTime(message.created_at)}</span>
                              {isAdmin && message.is_read && (
                                <CheckCircle className="w-3 h-3 text-green-400" />
                              )}
                            </div>
                          </div>
                        </motion.div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <form onSubmit={handleSendMessage} className="p-4 bg-white border-t border-gray-200">
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder={`Répondre en tant que ${channelInfo?.name}...`}
                      className="flex-1 px-4 py-3 bg-gray-100 border-0 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-colors"
                      disabled={sending}
                      data-testid="admin-chat-input"
                    />
                    <Button 
                      type="submit" 
                      disabled={!newMessage.trim() || sending}
                      className={`px-6 bg-gradient-to-r ${channelInfo?.color}`}
                      data-testid="admin-send-btn"
                    >
                      <Send className="w-5 h-5" />
                    </Button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
